<script lang="ts">
export default {
    name: 'BaseStatusBar',
};
</script>

<script lang="ts" setup>
import ExpandMode from './expand-mode.vue';
import FoldMode from './fold-mode.vue';

interface Props {
    /**
     * 标题
     */
    title?: string;
    /**
     * 指展开形态与收起状态切换后的背景色
     */
    background?: string;
    /**
     * 是否显示返回按钮
     */
    showBack?: boolean;
    /**
     * 是否显示分享
     */
    showShare?: boolean;
    /**
     * 挂件形态的icon颜色
     */
    expandIconColor?: string;
    /**
     * 折叠状态的图标颜色
     */
    foldIconColor?: string;
    /**
     * 滚动切换临界值
     */
    scrollEdge?: number;
    /**
     * 触发滚动事件的阈值
     */
    scrollThreshold?: number;
    /**
     * 只展示挂件状态
     */
    expand?: boolean;
    /**
     * 只展示折叠状态
     */
    fold?: boolean;
    /**
     * 收起状态切换后的背景
     */
    foldSwitchBackground?: string;
    /**
     * 收起状态切换后的图标以及标题色
     */
    foldSwitchIconColor?: string;
    /**
     * 返回前执行的回调
     */
    beforeBack?: (() => void) | (() => Promise<void>);
    /**
     * 自定义返回
     */
    customBack?: () => void;
    /**
     * 手动指定statusBarHeight
     */
    statusBarHeight?: number;
    /**
     * 是否在搜索-1tab
     */
    atSearchTab?: boolean;
}

withDefaults(defineProps<Props>(), {
    fold: false,
    expand: false,
    scrollThreshold: 10,
    scrollEdge: 100,
    background: '#fff',
    showShare: true,
    showBack: true,
});

const emit = defineEmits<{
    (event: 'share'): void;
    (event: 'total-height', val: number): void;
    (event: 'status-bar-height', val: number): void;
    (event: 'opacity', val: number): void;
}>();

function totalHeightHandle(val: number) {
    emit('total-height', val);
}

function statusBarHeightHandle(val: number) {
    emit('status-bar-height', val);
}

function opacityHandler(val: number) {
    emit('opacity', val);
}
</script>

<template>
    <FoldMode
        v-if="fold"
        :title="title"
        :show-back="showBack"
        :show-share="showShare"
        :icon-color="foldIconColor"
        :background="background"
        :switch-background="foldSwitchBackground"
        :switch-icon-color="foldSwitchIconColor"
        :scroll-edge="scrollEdge"
        :scroll-threshold="scrollThreshold"
        :before-back="beforeBack"
        :custom-back="customBack"
        :status-bar-height="statusBarHeight"
        @total-height="totalHeightHandle"
        @status-bar-height="statusBarHeightHandle"
        @opacity="opacityHandler"
        @share="emit('share')"
    >
        <template #title-fold>
            <slot name="title-fold" />
        </template>
        <template #left-fold>
            <slot name="left-fold" />
        </template>
        <template #right-fold>
            <slot name="right-fold" />
        </template>
        <template #title-switch-fold>
            <slot name="title-switch-fold" />
        </template>
        <template #left-switch-fold>
            <slot name="left-switch-fold" />
        </template>
        <template #right-switch-fold>
            <slot name="left-switch-fold" />
        </template>
    </FoldMode>
    <ExpandMode
        v-else
        :title="title"
        :expand="expand"
        :background="background"
        :show-back="showBack"
        :show-share="showShare"
        :icon-color="expandIconColor"
        :fold-icon-color="foldIconColor"
        :scroll-edge="scrollEdge"
        :scroll-threshold="scrollThreshold"
        :before-back="beforeBack"
        :custom-back="customBack"
        :status-bar-height="statusBarHeight"
        :at-search-tab="atSearchTab"
        @share="emit('share')"
        @total-height="totalHeightHandle"
        @status-bar-height="statusBarHeightHandle"
        @opacity="opacityHandler"
    >
        <template #left>
            <slot name="left" />
        </template>
        <template #right>
            <slot name="right" />
        </template>
        <template #mainTitleTop>
            <slot name="mainTitleTop" />
        </template>
        <template #mainTitle>
            <slot name="mainTitle" />
        </template>
        <template #mainTitleBottom>
            <slot name="mainTitleBottom" />
        </template>
    </ExpandMode>
</template>
